import { Avatar, Box, Button, Chip, FormControl, FormHelperText, InputLabel, MenuItem, OutlinedInput, Select, TextField, Tooltip } from '@mui/material';
import { DataGrid, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { useEffect, useMemo, useState } from 'react';
import { TiUserAdd } from "react-icons/ti";
import { MdOutlineEdit } from 'react-icons/md';
import { RiDeleteBin5Line } from "react-icons/ri";
import { FaCheckCircle } from "react-icons/fa";
import { GrInProgress } from "react-icons/gr";
import { IoIosCloseCircle } from "react-icons/io";
import { UserAPI } from '../../../api/UserAPI';
import { useSelector } from 'react-redux';
import { Privileges } from '../../../util/PrivilegeConstants';
import { useDispatch } from 'react-redux';
import { rootAction } from '../../../redux/reducer/rootReducer';
import { PrivilegeAPI } from '../../../api/PrivilegesAPI';
import * as Yup from 'yup';
import CustomDialog from '../../CustomComponents/CustomDialog/CustomDialog.jsx'
import { formatRoleLabel, hasAccessManagementRole, toManagePlayerRow, toRoleApiName } from '../../../util/UserRoles';
import defaultProfileImg from '../../../assets/images/default_profile_img.png';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const memberGridSx = {
    '& .MuiDataGrid-columnHeaders': {
        backgroundColor: '#f5f5f5',
        borderBottom: '2px solid #e8e8e8',
        fontWeight: 600,
        color: '#333',
        fontSize: '14px',
        textTransform: 'uppercase',
    },
    '& .MuiDataGrid-columnSeparator': {
        visibility: 'visible',
        color: '#d1d1d1',
    },
    '& .MuiDataGrid-cell': {
        borderBottom: '1px solid #f0f0f0',
        padding: '8px 16px',
        fontSize: '14px',
        color: '#333',
    },
    '& .MuiDataGrid-row:hover': {
        backgroundColor: '#f5f9ff',
    },
    '& .MuiDataGrid-footerContainer': {
        borderTop: '1px solid #e8e8e8',
        padding: '8px 16px',
        backgroundColor: '#fafafa',
    },
    '& .MuiDataGrid-toolbarContainer': {
        padding: 0,
    },
    border: 'none',
    borderRadius: '8px',
    '& .MuiDataGrid-menuIcon': {
        display: 'none !important',
    },
    '& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon': {
        display: 'none !important',
    },
    '& .MuiDataGrid-columnHeader:focus': {
        outline: 'none',
    },
};

const ellipsisCell = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
    width: '100%',
};

const CustomToolbar = ({ quickFilterProps, onAddMember, canAdd }) => {
    return (
        <div style={{
            width: '100%',
            backgroundColor: '#fafafa',
            borderBottom: '1px solid #e8e8e8',
            padding: '8px 16px',
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '16px',
            }}>
                <GridToolbarQuickFilter
                    {...quickFilterProps}
                    sx={{
                        '& .MuiInputBase-root': {
                            borderRadius: '8px',
                            height: '38px',
                            backgroundColor: 'white',
                            width: '320px',
                        },
                        '& .MuiInputBase-input': {
                            fontSize: '14px',
                            padding: '0 12px',
                        },
                    }}
                    placeholder="Search by Name, Email, Phone, or Role..."
                    debounceMs={400}
                    quickFilterParser={(searchInput) =>
                        searchInput.split(',').map((value) => value.trim())
                    }
                />
                {canAdd && (
                    <Button
                        variant="contained"
                        onClick={onAddMember}
                        startIcon={<TiUserAdd />}
                        sx={{
                            textTransform: 'none',
                            backgroundColor: 'var(--hoverColorBold)',
                            '&:hover': { backgroundColor: 'var(--hoverColorBold)' },
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Add member
                    </Button>
                )}
            </div>
        </div>
    );
};

const validationObj = Yup.object({
    firstName: Yup.string().required("First Name is required"),
    lastName: Yup.string().required("Last Name is required"),
    email: Yup.string().required("Email is required").email("Must be valid email"),
    role: Yup.array().min(1, "Select atleast 1 role")
})

const InviteStaff = () => {
    const userInfo = useSelector((store) => store.user.userInfo);
    const selectedClub = useSelector((store) => store.user.selectedClub);
    const [openDialog, setOpenDialog] = useState({ isOpen: false, isEdit: false });
    const [loading, setLoading] = useState(false);
    const [allMember, setAllMember] = useState([])
    const [memberData, setMemberData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: ['STAFF']
    });
    const [errors, setErrors] = useState({});
    const [privileges, setPrivileges] = useState({});
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
    const dispatch = useDispatch();

    useEffect(() => {
        if (selectedClub.clubId) {
            fetchAllMember();
        }
    }, [selectedClub.clubId]);

    const findAllPrivileges = async () => {
        try {
            setLoading(true)
            const reqObj = {
                userId: userInfo.userId,
                featureName: Object.keys(Privileges)[10]
            }
            const response = await PrivilegeAPI.getAllPrivilegesByFeature(reqObj);
            setPrivileges(response.Data);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userInfo.userId) {
            findAllPrivileges();
        }
    }, [userInfo.userId]);

    const fetchAllMember = async () => {
        try {
            setLoading(true);
            const res = await UserAPI.getAllMembersForAClub(selectedClub.clubId,0,100,'',{})
            setAllMember((res.Data || []).filter((member) => hasAccessManagementRole(member.roles)));
            setLoading(false);
        } catch (error) {
            setLoading(false);
        }
    }

    const handleCreateMemberUser = async () => {
        try {
            if (!memberData.role?.length) {
                setErrors({ role: "Select atleast 1 role" });
                return;
            }
            await validationObj.validate(memberData, { abortEarly: false })
            const userDetails = {
                firstName: memberData.firstName?.trim(),
                lastName: memberData.lastName?.trim(),
                mobile: memberData.phone ? memberData.phone?.startsWith('+') ? memberData?.phone : "+" + memberData?.phone : "+1000000000",
                email: memberData.email.trim(),
                club: {
                    clubId: selectedClub.clubId
                },
                roleNames: memberData.role
            }

            setLoading(true);
            const response = await UserAPI.createMemberUser(userDetails);
            setAllMember(prev => [...prev, response.Data]);
            setMemberData({ role: ['STAFF'] });
            Object.keys(errors).length != 0 && setErrors({});
            setOpenDialog({ isOpen: false, isEdit: false });
            dispatch(rootAction.setWarningMessage({ id: Date.now(), type: 'success', content: 'Invitation sent successfully' }));
        } catch (error) {
            console.log(error);

            if (error.inner) {
                let errObj = {};
                error.inner.forEach(err => {
                    errObj[err.path] = err.message;
                })
                setErrors(errObj);
            } else {
                dispatch(rootAction.setWarningMessage({ id: Date.now(), type: 'error', content: error?.response?.data?.Data }));
            }
        } finally {
            setLoading(false);
        }
    }

    const handleMemberData = (event) => {
        const { name, value } = event.target;
        if (name === 'role') {
            if (memberData.role.includes('STAFF')) {
                if(value.includes('STAFF') && value.includes('COACH')){
                    const newValue = value.filter(item => item !== 'STAFF');
                    setMemberData({ ...memberData, [name]: newValue})
                }else{
                    setMemberData({
                        ...memberData, [name]: value
                    });
                }
            } else if (memberData.role.includes('COACH')) {
                if (value.includes('STAFF') && value.includes('COACH')) {
                    const newValue = value.filter(item => item !== 'COACH');
                    setMemberData({ ...memberData, [name]: newValue })
                }else{
                    setMemberData({
                        ...memberData, [name]: value
                    });
                }
            } else {
                setMemberData({
                    ...memberData, [name]: value
                });
            }
            Object.keys(errors).length != 0 && setErrors({});
        } else {
            // Clean phone number (only digits and +)
            let newVal = value;
            if (name === 'phone') {
                newVal = value.replace(/[^\d+]/g, '');
                // Limit to 15 characters
                if (newVal.length > 15) {
                    newVal = newVal.substring(0, 15);
                }
            }
            setMemberData({ ...memberData, [name]: newVal })
            Object.keys(errors).length != 0 && setErrors({});
        }
    }

    const handleClickOpen = () => {
        setOpenDialog({ isOpen: true, isEdit: false });
    };

    const handleClose = () => {
        Object.keys(errors).length != 0 && setErrors({});
        setOpenDialog({ isOpen: false, isEdit: false });
        setMemberData({ role: ['STAFF'] })
    };

    const handleOpenEditPopup = (userId, role) => {
        setMemberData({ userId: userId, role: (role || []).map(toRoleApiName).filter((name) => name === 'STAFF' || name === 'COACH') })
        setOpenDialog({ isOpen: true, isEdit: true });
    };

    const handleEditRole = async () => {
        try {
            if (!memberData.role?.length) {
                setErrors({ role: "Select atleast 1 role" });
                return;
            }
            setLoading(true);
            await UserAPI.updateUserRole(memberData.userId, { roleNames: memberData.role });
            setAllMember(prev => prev.map(item => item.userId === memberData.userId ? { ...item, roles: memberData.role.map((name) => name.toLowerCase()) } : item))
            setOpenDialog({ isOpen: false, isEdit: false });
            setMemberData({ role: ['STAFF'] });
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const deleteMember = async (userId) => {
        try {
            setLoading(true);
            await UserAPI.deleteClubMember(userId, selectedClub?.clubId);
            setAllMember(prev => prev.filter(item => item.userId !== userId));
            dispatch(rootAction.setWarningMessage({ id: Date.now(), type: 'success', content: 'Member deleted successfully' }));
        } catch (error) {
            dispatch(rootAction.setWarningMessage({ id: Date.now(), type: 'error', content: error?.response?.data?.Data ? error?.response?.data?.Data : 'Please contact customer support' }));
        } finally {
            setLoading(false);
        }
    }

    const columns = useMemo(() => [
        {
            field: 'profileImage',
            headerName: 'Profile',
            flex: 0.5,
            minWidth: 70,
            maxWidth: 100,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Tooltip title={params.row.playerName || ''} arrow>
                    <Avatar
                        src={params.value ? `${import.meta.env.VITE_APP_BASE_URL}/user/user-profile-img/${params.value}` : defaultProfileImg}
                        sx={{ width: 40, height: 40 }}
                    />
                </Tooltip>
            )
        },
        {
            field: 'playerName',
            headerName: 'Name',
            flex: 1.2,
            minWidth: 150,
            maxWidth: 250,
            sortable: true,
            filterable: false,
            renderCell: (params) => (
                <Tooltip title={params.value} arrow>
                    <span style={ellipsisCell}>{params.value}</span>
                </Tooltip>
            )
        },
        {
            field: 'roleName',
            headerName: 'Role',
            flex: 0.8,
            minWidth: 140,
            maxWidth: 200,
            sortable: true,
            filterable: false,
            renderCell: (params) => {
                const canEditRole = privileges?.MANAGE_INVITE_STAFF?.includes(Privileges.MODIFY);
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Chip label={formatRoleLabel(params.value)} size="small" />
                        {canEditRole && (
                            <Tooltip title="Change role" arrow>
                                <MdOutlineEdit
                                    data-testid="change-role"
                                    style={{ cursor: 'pointer', fontSize: '18px' }}
                                    onClick={() => handleOpenEditPopup(params.row.playerId, params.row.roles)}
                                />
                            </Tooltip>
                        )}
                    </div>
                );
            },
        },
        {
            field: 'playerStatus',
            headerName: 'Status',
            flex: 0.8,
            minWidth: 120,
            maxWidth: 180,
            sortable: true,
            filterable: false,
            renderCell: (params) => {
                const val = params.value;
                if (val === 'INACTIVE') {
                    return (
                        <Chip
                            icon={<IoIosCloseCircle />}
                            label="Inactive"
                            color="error"
                            size="small"
                        />
                    );
                }
                return (
                    <Chip
                        icon={val === 'ACTIVE' ? <FaCheckCircle /> : <GrInProgress />}
                        label={val === 'ACTIVE' ? 'Active' : 'Pending'}
                        color={val === 'ACTIVE' ? 'success' : 'warning'}
                        size="small"
                    />
                );
            }
        },
        {
            field: 'email',
            headerName: 'Email',
            flex: 1.2,
            minWidth: 180,
            maxWidth: 300,
            sortable: true,
            filterable: false,
            renderCell: (params) => (
                <Tooltip title={params.value || 'N/A'} arrow>
                    <span style={ellipsisCell}>{params.value || 'N/A'}</span>
                </Tooltip>
            )
        },
        {
            field: 'phone',
            headerName: 'Phone',
            flex: 0.9,
            minWidth: 140,
            maxWidth: 200,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Tooltip title={params.value || 'N/A'} arrow>
                    <span style={ellipsisCell}>{params.value || 'N/A'}</span>
                </Tooltip>
            )
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 0.5,
            minWidth: 90,
            maxWidth: 120,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                if (!privileges?.MANAGE_INVITE_STAFF?.includes(Privileges.DELETE)) return null;
                return (
                    <Tooltip title="Remove member" arrow>
                        <RiDeleteBin5Line
                            data-testid="delete-member"
                            style={{ cursor: 'pointer', fontSize: '18px', color: '#d32f2f' }}
                            onClick={() => deleteMember(params.row.playerId)}
                        />
                    </Tooltip>
                );
            }
        },
    ], [privileges]);

    const rowsWithId = allMember.map((member, index) => {
        const row = toManagePlayerRow(member);
        return {
            ...row,
            id: row.playerId || member.userId || `row-${index}`,
        };
    });

    return (
        <div className="member-list-container">
            <div className="add-member-heading">
                <h1>Manage Internal Members</h1>
            </div>
            <p className='invite_staff_page_intro'>
                Invite and manage people who run the club — staff, coaches, and other club roles you add later.
                Players stay on Manage Players.
            </p>
            <div className="member-list-table">
                <div style={{ height: 850, width: '100%', background: 'white', borderRadius: '8px', padding: '16px' }}>
                    <DataGrid
                        rows={rowsWithId}
                        columns={columns}
                        pagination
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pageSizeOptions={[50, 100]}
                        loading={loading}
                        disableSelectionOnClick
                        getRowId={(row) => row.id}
                        disableColumnResize={false}
                        slots={{
                            toolbar: () => (
                                <CustomToolbar
                                    quickFilterProps={{
                                        debounceMs: 400,
                                    }}
                                    onAddMember={handleClickOpen}
                                    canAdd={privileges?.MANAGE_INVITE_STAFF?.includes(Privileges.SEND)}
                                />
                            ),
                        }}
                        localeText={{ noRowsLabel: 'No internal members found' }}
                        hideFooterPagination={false}
                        sx={memberGridSx}
                    />
                </div>
            </div>

            <CustomDialog
                title={openDialog.isEdit ? "Edit Member Role" : "Add Staff"}
                isOpen={openDialog.isOpen}
                onClose={() => handleClose()}
                onCancel={() => handleClose()}
                onSubmit={openDialog.isEdit ? handleEditRole : handleCreateMemberUser}
                submitBtnName={openDialog.isEdit ? 'Save' : 'Invite'}
            >
                <div className="add-member-form">
                    {!openDialog.isEdit &&
                        <>
                            <div className="member-name-input">
                                <TextField
                                    className="add-member-text-input"
                                    error={!!errors.firstName}
                                    helperText={errors.firstName && errors.firstName}
                                    variant="outlined"
                                    name="firstName"
                                    inputProps={{ maxLength: 100 }}
                                    value={memberData.firstName || ''}
                                    onChange={handleMemberData}
                                    label="First Name *" 
                                    fullWidth
                                />
                                <TextField
                                    className="add-member-text-input"
                                    error={!!errors.lastName}
                                    inputProps={{ maxLength: 100 }}
                                    helperText={errors.lastName && errors.lastName}
                                    variant="outlined"
                                    value={memberData.lastName || ''}
                                    onChange={handleMemberData}
                                    name="lastName"
                                    label="Last Name *" 
                                    fullWidth
                                />
                            </div>
                            <div className="member-email-input">
                                <TextField 
                                    fullWidth
                                    className="add-member-text-input"
                                    error={!!errors.email}
                                    inputProps={{ maxLength: 100 }}
                                    helperText={errors.email && errors.email}
                                    variant="outlined"
                                    value={memberData.email || ''}
                                    onChange={handleMemberData}
                                    label='Email *'
                                    name='email' 
                                />
                            </div>
                            <div className="member-email-mobile">
                                <TextField
                                    fullWidth
                                    className="add-member-text-input"
                                    error={!!errors.phone}
                                    helperText={errors.phone && errors.phone}
                                    variant="outlined"
                                    name="phone"
                                    inputProps={{ maxLength: 15 }}
                                    value={memberData.phone || ''}
                                    onChange={handleMemberData}
                                    label="Phone"
                                    placeholder="+1234567890"
                                />
                            </div>
                        </>
                    }
                    <FormControl sx={{ width: '100%' }}
                        className="signup-text-input"
                        error={!!errors.role}
                    >
                        <InputLabel id="demo-multiple-chip-label">Role</InputLabel>
                        <Select
                            labelId="demo-multiple-chip-label"
                            id="demo-multiple-chip"
                            multiple
                            value={memberData.role ?? []}
                            name='role'
                            onChange={handleMemberData}
                            input={<OutlinedInput id="select-multiple-chip" label="Role" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip key={value} onMouseDown={(event) => event.stopPropagation()} label={formatRoleLabel(value)} clickable={true} onDelete={() => {
                                            setMemberData((prev) => ({
                                                ...prev,
                                                role: (prev.role || []).filter((item) => item !== value),
                                            }));
                                        }} />
                                    ))}
                                </Box>
                            )}
                            MenuProps={MenuProps}
                        >
                            <MenuItem value="STAFF">Staff</MenuItem>
                            <MenuItem value="COACH">Coach</MenuItem>
                        </Select>
                        {errors.role && (
                            <FormHelperText sx={{ color: "red" }}>
                                {errors.role}
                            </FormHelperText>
                        )}
                    </FormControl>
                </div>
            </CustomDialog>
        </div>
    )
}

export default InviteStaff
